import { Button } from '#app/components/Buttons/Button.js';
import { ErrorBoundary } from '#app/components/ErrorBoundary/ErrorBoundary.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { type Variant } from './types.js';
import { useProfileMagicBannerCTA } from './useProfileMagicBannerCTA.js';
import { SparklesEmoji } from '@contra/icons/emojis';
import { ChevronRightIcon, CloseIcon } from '@contra/icons/icons';
import { css, styled } from 'styled-components';

type ProfileMagicBannerCTAProps = {
  readonly displayUsername: string;
  readonly variant: Variant;
  readonly visitorCanEdit: boolean;
};

const DesktopContainer = styled.div`
  ${() => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px;
    margin-bottom: 16px;

    width: 100%;
    min-height: 32px;
    height: fit-content;

    border-radius: 10px;
    background: linear-gradient(
      90deg,
      rgba(243, 202, 77, 0.2) 0%,
      rgba(255, 152, 154, 0.2) 42.88%,
      rgba(203, 171, 255, 0.2) 94.62%
    );
  `}
`;

const SuggestedText = styled(Text)``;

const TryPortfolioMagicTextLink = styled(Text)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    white-space: nowrap;
    font-weight: 600;

    color: ${theme.colorsV2.gray80};
    cursor: pointer;

    :hover {
      text-decoration: underline;
    }
  `}
`;

const StyledCloseIcon = styled(CloseIcon)`
  margin-left: auto;
  cursor: pointer;
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  padding: 16px 0px;
  margin-bottom: 16px;

  background:
    linear-gradient(white, white) padding-box,
    linear-gradient(
        90deg,
        rgba(243, 202, 77, 1),
        rgba(255, 152, 154, 1),
        rgba(203, 171, 255, 1)
      )
      border-box;
  border: 1.5px solid transparent;
  border-left: 0;
  border-right: 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const StyledSparklesEmoji = styled(SparklesEmoji)`
  margin-right: 8px;
`;

/**
 * @package
 */
export const ProfileMagicBannerCTA = ({
  displayUsername,
  variant,
  visitorCanEdit,
}: ProfileMagicBannerCTAProps) => {
  const { CTAHref, handleCTAClick, handleCTADismiss, shouldShowCTA } =
    useProfileMagicBannerCTA({ displayUsername, variant, visitorCanEdit });

  const isMobile = useIsMobile();

  if (!shouldShowCTA) {
    return null;
  }

  if (isMobile) {
    return (
      <ErrorBoundary
        boundaryIdentifier="profile-magic-banner-cta-mobile"
        renderError={() => null}
      >
        <MobileContainer>
          <SuggestedText>
            <StyledSparklesEmoji size={16} />
            {`Need ${variant} ideas?`}
          </SuggestedText>
          <Row>
            <TryPortfolioMagicTextLink
              as="a"
              href={CTAHref}
              onClick={handleCTAClick}
              textStyle="bodyRegular"
            >
              Generate AI suggestions <ChevronRightIcon size={24} />
            </TryPortfolioMagicTextLink>
            <Button
              aria-label="Dismiss"
              marginLeft="auto"
              onClick={handleCTADismiss}
              size="small"
              variant="V2TertiaryOutline"
            >
              Close
            </Button>
          </Row>
        </MobileContainer>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      boundaryIdentifier="profile-magic-banner-cta-desktop"
      renderError={() => null}
    >
      <DesktopContainer>
        <SuggestedText textStyle="bodyRegular">
          <StyledSparklesEmoji size={16} />
          {`Need ${variant} ideas?`}
        </SuggestedText>
        <TryPortfolioMagicTextLink
          as="a"
          href={CTAHref}
          onClick={handleCTAClick}
          textStyle="bodyRegular"
        >
          Generate AI suggestions <ChevronRightIcon size={24} />
        </TryPortfolioMagicTextLink>
        <StyledCloseIcon
          aria-label="Dismiss"
          onClick={handleCTADismiss}
          size={24}
        />
      </DesktopContainer>
    </ErrorBoundary>
  );
};
